/**
 * Shared helpers for programmatic map authoring.
 *
 * Terrain and routes stay separate: route cells are authored first, then the
 * grid is painted from them. Nothing here derives routes from tiles.
 */

import type { GridPosition, MapRoute } from '../model/types'

export function emptyGrid(width: number, height: number, fill = 'G'): string[][] {
  return Array.from({ length: height }, () => fill.repeat(width).split(''))
}

export function setPath(grid: string[][], cells: GridPosition[]): void {
  for (const c of cells) {
    if (grid[c.y] && grid[c.y]![c.x] !== undefined) grid[c.y]![c.x] = 'P'
  }
}

export function horiz(y: number, x0: number, x1: number): GridPosition[] {
  const cells: GridPosition[] = []
  const step = x1 >= x0 ? 1 : -1
  for (let x = x0; x !== x1 + step; x += step) cells.push({ x, y })
  return cells
}

export function vert(x: number, y0: number, y1: number): GridPosition[] {
  const cells: GridPosition[] = []
  const step = y1 >= y0 ? 1 : -1
  for (let y = y0; y !== y1 + step; y += step) cells.push({ x, y })
  return cells
}

/** Concatenate segments, dropping the duplicated corner cell between them. */
export function joinPaths(segments: GridPosition[][]): GridPosition[] {
  const out: GridPosition[] = []
  for (const seg of segments) {
    for (const c of seg) {
      const last = out[out.length - 1]
      if (last && last.x === c.x && last.y === c.y) continue
      out.push(c)
    }
  }
  return out
}

export function rowsFromGrid(grid: string[][]): string[] {
  return grid.map((row) => row.join(''))
}

/** Ground lane whose spawn/exit are the first/last authored cells. */
export function groundRoute(id: string, cells: GridPosition[]): MapRoute {
  return {
    id,
    movementClass: 'GROUND',
    spawn: { ...cells[0]! },
    exit: { ...cells[cells.length - 1]! },
    cells: cells.map((c) => ({ ...c }))
  }
}

/** Air lane from sparse waypoints — may jump cells and ignore occupancy. */
export function airRoute(id: string, cells: GridPosition[]): MapRoute {
  return {
    id,
    movementClass: 'AIR',
    spawn: { ...cells[0]! },
    exit: { ...cells[cells.length - 1]! },
    cells: cells.map((c) => ({ ...c }))
  }
}
